import type { ExamFlag } from "@/api/db/connection";
import { actionError } from "@/lib/error-code";

const examFlagLabel: Record<ExamFlag, string> = {
	normal: "Normal",
	low: "Baixo",
	high: "Alto",
	attention: "Atenção",
	indeterminate: "Indeterminado",
};

const examFlagTone: Record<ExamFlag, string> = {
	normal: "text-success",
	low: "text-warning",
	high: "text-destructive",
	attention: "text-destructive",
	indeterminate: "text-muted-foreground",
};

const examConcordanceLabel: Record<string, string> = {
	double: "Dupla leitura",
	double_with_caveat: "Dupla com ressalva",
	divergent: "Divergente",
	single_reader: "Leitor único",
	human: "Editado pela Dra.",
};

const examStatusLabel: Record<string, string> = {
	pending: "Em leitura",
	read: "Lido",
	divergent: "Data divergente",
	illegible: "Ilegível",
	error: "Falha na leitura",
	identity_blocked: "Identidade a confirmar",
};

const examConcordanceBadge: Record<string, string> = {
	human: "Dra.",
	single_reader: "1 leitor",
	double_with_caveat: "ressalva",
};

const examScoreReasonLabel: Record<string, string> = {
	patient_document: "Documento enviado pela paciente",
	patient_image: "Imagem enviada pela paciente",
	pdf: "Arquivo PDF",
	photo: "Foto",
	filename_match: "Nome do arquivo sugere exame",
	text_match: "Mensagem menciona exame",
};

const examErrorLabel: Record<string, string> = {
	QUEUE_ENTRY_NOT_FOUND: "Sugestão não encontrada.",
	NOT_AN_EXAM: "Esta mensagem não é um exame.",
	UNSUPPORTED_MIME: "Formato de arquivo não suportado.",
	ANCHOR_NOT_FOUND: "Paciente não encontrada.",
	MEDIA_EXPIRED: "Arquivo original expirado.",
	ALREADY_REGISTERED: "Exame já registrado.",
	DOCUMENT_NOT_FOUND: "Documento não encontrado.",
	RESULT_NOT_FOUND: "Resultado não encontrado.",
	READING_NOT_FOUND: "Leitura do modelo não encontrada.",
	INVALID_STATUS: "Ação indisponível para o estado atual do documento.",
	IDENTITY_UNRESOLVED: "Identidade da paciente não resolvida.",
	REPORT_IDENTITY_MISMATCH: "Identidade do laudo divergente da paciente.",
	ALREADY_ANCHORED: "Documento já vinculado a uma paciente.",
	FILE_MISSING: "Arquivo do documento indisponível.",
};

export const ExamLabels = {
	flag: examFlagLabel,
	flagTone: examFlagTone,
	editField: {
		title: "Editar resultado",
		valueNum: "Valor numérico",
		valueText: "Valor textual",
		unit: "Unidade",
		refRange: "Faixa de referência",
		manual: "editar resultado",
		save: "salvar",
		cancel: "cancelar",
	},
	readingModel: {
		header: "Usar leitura de",
		choose: "escolher leitura",
	},
	editedBy(name: string) {
		return `editado por ${name}`;
	},
	confirmIdentity: {
		trigger: "confirmar identidade",
		prompt: "Confirmar a identidade deste laudo?",
		pending: "confirmando...",
	},
	upload: {
		unsupportedFormat: "Formato não suportado (PDF, JPEG, PNG ou WebP).",
		registered: "Exame registrado.",
		registerAction: "Registrar exame",
	},
	concordance(value: string) {
		return examConcordanceLabel[value] ?? value;
	},
	concordanceBadge(value: string) {
		return examConcordanceBadge[value];
	},
	status(value: string) {
		return examStatusLabel[value] ?? value;
	},
	scoreReason(value: string) {
		return examScoreReasonLabel[value] ?? value;
	},
	error(code: string | undefined) {
		return actionError(examErrorLabel, code);
	},
	suggested: {
		title: "Exames sugeridos",
		subtitle: "documentos da conversa",
		emptyTitle: "Nenhum exame sugerido",
		emptyDescription: "Nenhum documento dos últimos 10 dias.",
		register: "Registrar exame",
		open: "abrir documento",
		discard: "descartar",
		discarding: "descartando...",
		discardConfirm: "Descartar esta sugestão de exame? Não há como recuperar.",
		discardCancel: "cancelar",
		searchPlaceholder: "De qual paciente é este exame?",
		needsPatient: "Paciente sem cadastro civil.",
		unnamedContact: "Contato sem nome",
	},
	withoutOwner: {
		title: "Exames sem paciente",
		subtitle: "sem vínculo",
		emptyTitle: "Nenhum exame sem paciente",
		emptyDescription: "Todos os documentos estão vinculados.",
		anchor: "Vincular exame",
		confirmSame: "É a mesma pessoa?",
		confirmingSame: "confirmando...",
		searchPlaceholder: "Buscar paciente",
		unknownReportName: "titular não identificado no laudo",
	},
};
